import React, { useEffect, useState } from 'react';
import { atom, useRecoilState, useRecoilValue } from 'recoil';

import axios from 'axios';
import { ResponsiveLine } from '@nivo/line';
import { useOrdinalColorScale } from '@nivo/colors';
import { useTooltip } from '@nivo/tooltip';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Tab, Tabs } from '../components/Navigation/Tabs';

import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DateAdapter from '@mui/lab/AdapterMoment';
import DatePicker from '@mui/lab/DatePicker';

import moment from 'moment';
import 'moment/locale/ko';
import FormControlLabel from '@mui/material/FormControlLabel';

moment.locale('ko');

const sliceState = atom({
  key: 'sliceState', // unique ID (with respect to other atoms/selectors)
  default: {} // default value (aka initial value)
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 16;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

export default function LogStat() {
  const stateValue = useRecoilValue(sliceState);

  const date = new Date();

  const [progItem, setProgItem] = useState([]);
  const [progSelected, setProgSelected] = useState([]);
  const [compItem, setCompItem] = useState([]);
  const [compSelected, setCompSelected] = useState([]);
  const [rows, setRows] = useState([]);
  const [rowsSelected, setRowsSelected] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [srchData, setSrchData] = useState({
    startYmd: moment(date.getTime()).add('-10', 'd'),
    endYmd: moment(),
    gbn: 'day',
    isDohwa: true,
    isResearcher: true,
    isDuplicate: true
  });

  const isAllProgSelected = progItem.length > 0 && progSelected.length === progItem.length;
  const isAllCompSelected = compItem.length > 0 && compSelected.length === compItem.length;

  const handleChangeMultiSelect = (event) => {
    const {
      target: { value, name }
    } = event;

    if (value[value.length - 1] === 'all') {
      if (name === 'pgm') {
        const items = progItem.map((t) => t.engNm);
        setProgSelected(progSelected.length === progItem.length ? [] : items);
      } else if (name === 'cpname') {
        const items = compItem.map((t) => t.cdVal);
        setCompSelected(compSelected.length === compItem.length ? [] : items);
      }
      return;
    }
    name === 'pgm' ? setProgSelected(value) : setCompSelected(value);
  };

  const handleChangeSelect = (e) => {
    const gbn = e.target.value;
    let stdYmd = srchData.endYmd.clone();

    if (gbn === 'year') {
      setSrchData({ ...srchData, [e.target.name]: e.target.value, startYmd: stdYmd.add('-10', 'y') });
    } else if (gbn === 'month') {
      stdYmd.add('-1', 'y');
      stdYmd.add('1', 'd'); //선택한 월까지 나오게 해야함
      setSrchData({ ...srchData, [e.target.name]: e.target.value, startYmd: stdYmd });
    } else {
      stdYmd.add('-15', 'd');
      setSrchData({ ...srchData, [e.target.name]: e.target.value, startYmd: stdYmd });
    }
  };

  const handleChangeTab = async (event, newValue) => {
    await setSrchData({ ...srchData, isDohwa: true });
    await setSelectedTab(newValue);
  };

  const handleChangeCheckbox = (e) => {
    setSrchData({ ...srchData, [e.target.name]: e.target.checked });
  };

  const handleChangeIsDohwa = (e) => {
    setSrchData({ ...srchData, isDohwa: e.target.value });
  };

  const vaildate = (gbn) => {
    const { startYmd, endYmd } = srchData;
    let isValid = false;

    if (gbn === 'year' && endYmd.diff(startYmd, 'years') > 10) {
      alert(`년 검색기간은 10년까지 가능합니다.`);
      return isValid;
    }
    if (gbn === 'month' && endYmd.diff(startYmd, 'years') > 0) {
      alert(`월 검색기간은 12개월까지 가능합니다.`);
      return isValid;
    }

    return true;
  };

  /**
   * 제품 목록 조회
   * @param {*} e
   */
  const getProgList = async (e) => {
    const response = await axios.get(`/api/pgm`, { params: { kind: 'public' } });
    setProgItem(response.data);
    setProgSelected(response.data.map((t) => t.engNm));
  };

  /**
   * 회사 목록 조회
   * @param {*} e
   */
  const getCompList = async (e) => {
    const response = await axios.get(`/api/code/comp`, {});
    setCompItem(response.data);
    setCompSelected(response.data.map((t) => t.cdNm));
  };

  /**
   * 통계 검색 조회
   * @param {*} e
   */
  const getDataList = async (e) => {
    const { startYmd, endYmd, gbn, isDohwa, isResearcher, isDuplicate } = srchData;

    if (vaildate(gbn)) {
      let params = {
        startYmd: startYmd.format('YYYY-MM-DD'),
        endYmd: endYmd.format('YYYY-MM-DD'),
        gbn,
        progSelected,
        compSelected,
        isDohwa,
        isResearcher,
        isDuplicate,
        selectedTab
      };

      const response = await axios.get(`/api/stat/access`, { params });
      await setRows(response.data);
    }
  };

  const getData = async () => {
    const { gbn, isDohwa, isResearcher, isDuplicate } = srchData;
    const { x } = stateValue;

    if (vaildate(gbn)) {
      let params = {
        gbn,
        progSelected,
        compSelected,
        isDohwa,
        isResearcher,
        isDuplicate,
        selectYmd: x
      };

      const response = await axios.get(`/api/stat/detail`, { params });

      let chkPgm = [];
      let fLv = [];
      response.data.forEach((item) => {
        const pgm = item.pgm;
        if (!chkPgm.includes(pgm)) {
          chkPgm.push(pgm);
          fLv.push({ pgm: pgm, total: item.sum });
        }
      });
      const sLv = fLv.map((item) => {
        const list = response.data.filter((t) => t.pgm === item.pgm);
        item.data = list;
        return item;
      });
      setRowsSelected(sLv);
    }
  };

  useEffect(() => {
    if (isAllProgSelected && progItem !== []) {
      getDataList();
    }
  }, [isAllProgSelected]);

  useEffect(() => {
    getDataList();
  }, [selectedTab]);

  useEffect(() => {
    if (rows.length > 0) {
      getData();
    }
  }, [stateValue]);

  useEffect(() => {
    getProgList();
    getCompList();
  }, []);

  return (
    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
      <Typography component="h2" variant="h6" color="primary" gutterBottom>
        통계검색
      </Typography>
      <Tabs value={selectedTab} onChange={handleChangeTab} textColor="secondary" indicatorColor="secondary" sx={{ mb: 3 }}>
        <Tab label="제품별" index="0" />
        <Tab label="로그" index="1" />
      </Tabs>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack direction="row" spacing={2}>
            <FormControl>
              <InputLabel id="pgm-label">제품명</InputLabel>
              <Select labelId="pgm-label" id="pgm" name="pgm" multiple value={progSelected} input={<OutlinedInput label="제품명" />} renderValue={(selected) => selected.join(', ')} onChange={handleChangeMultiSelect} MenuProps={MenuProps}>
                <MenuItem key={`prog-all`} value={`all`}>
                  <Checkbox checked={isAllProgSelected} indeterminate={progSelected.length > 0 && progSelected.length < progItem.length} />
                  <ListItemText primary={`전체 선택`} />
                </MenuItem>
                {progItem.map((item, idx) => {
                  return (
                    <MenuItem key={`prog-${idx}`} value={item.engNm}>
                      <Checkbox checked={progSelected.indexOf(item.engNm) > -1} />
                      <ListItemText primary={item.engNm} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="pgm-label">회사명</InputLabel>
              <Select
                labelId="cpname-label"
                id="cpname"
                name="cpname"
                multiple
                value={compSelected}
                input={<OutlinedInput label="회사명" />}
                renderValue={(selected) => selected.join(', ')}
                onChange={handleChangeMultiSelect}
                MenuProps={MenuProps}
              >
                <MenuItem key={`prog-all`} value={`all`}>
                  <Checkbox checked={isAllCompSelected} indeterminate={compSelected.length > 0 && compSelected.length < compItem.length} />
                  <ListItemText primary={`전체 선택`} />
                </MenuItem>
                {compItem.map((item, idx) => {
                  return (
                    <MenuItem key={`prog-${idx}`} value={item.cdVal}>
                      <Checkbox checked={compSelected.indexOf(item.cdVal) > -1} />
                      <ListItemText primary={item.cdVal} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={2}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="gbn-label">검색기준</InputLabel>
              <Select labelId="gbn-label" id="gbn" name="gbn" value={srchData.gbn} label="검색기준" onChange={handleChangeSelect}>
                <MenuItem value={'year'}>년</MenuItem>
                <MenuItem value={'month'}>월</MenuItem>
                <MenuItem value={'day'}>일</MenuItem>
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={DateAdapter}>
              <DatePicker
                sx={{ minWidth: 250 }}
                inputFormat={'yyyy-MM-DD'}
                label="시작일"
                value={srchData.startYmd}
                onChange={(newValue) => {
                  setSrchData({ ...srchData, startYmd: newValue });
                }}
                renderInput={(params) => <TextField {...params} helperText={null} />}
                disableMaskedInput={true}
              />
              <DatePicker
                inputFormat={'yyyy-MM-DD'}
                label="종료일"
                value={srchData.endYmd}
                onChange={(newValue) => {
                  setSrchData({ ...srchData, endYmd: newValue });
                }}
                renderInput={(params) => <TextField {...params} helperText={null} />}
                disableMaskedInput={true}
              />
            </LocalizationProvider>
            {selectedTab === 1 && (
              <>
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel id="gbn-label">구분</InputLabel>
                  <Select labelId="gbn-label" id="gbn" name="gbn" value={srchData.isDohwa} label="검색기준" onChange={handleChangeIsDohwa}>
                    <MenuItem value={true}>타사</MenuItem>
                    <MenuItem value={'dohwaOnly'}>도화</MenuItem>
                    <MenuItem value={false}>도화 + 타사</MenuItem>
                    <MenuItem value={'allSplit'}>도화 & 타사</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
            {selectedTab === 0 && <FormControlLabel control={<Checkbox />} id="isDohwa" name="isDohwa" label="도화 제외" onChange={handleChangeCheckbox} checked={srchData.isDohwa} />}
            <FormControlLabel control={<Checkbox />} id="isResearcher" name="isResearcher" label="연구원 제외" onChange={handleChangeCheckbox} checked={srchData.isResearcher} />
            <FormControlLabel control={<Checkbox />} id="isDuplicate" name="isDuplicate" label="중복 제거" onChange={handleChangeCheckbox} checked={srchData.isDuplicate} />
            <Button variant="contained" onClick={getDataList}>
              검색
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Paper
            sx={{
              width: 1100,
              height: 500
            }}
          >
            <MyResponsiveLine data={rows} />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper
            sx={{
              width: 1100,
              height: 500
            }}
          >
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">제품명</TableCell>
                    <TableCell align="center">합계</TableCell>
                    <TableCell align="center">업체</TableCell>
                    <TableCell align="center">사용일자</TableCell>
                    <TableCell align="center">사용횟수</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowsSelected.map((item, idx) => {
                    return item.data.map((row, idx) => {
                      return (
                        <TableRow key={`row-${row}-${idx}`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          {idx === 0 && (
                            <TableCell component="th" align="center" scope="row" rowSpan={item.data.length}>
                              {item.pgm}
                            </TableCell>
                          )}
                          {idx === 0 && (
                            <TableCell align="center" rowSpan={item.data.length}>
                              {item.total}
                            </TableCell>
                          )}
                          <TableCell align="center">{row.cpname}</TableCell>
                          <TableCell align="center">{row.ymd}</TableCell>
                          <TableCell align="center">{row.ymdcnt}</TableCell>
                        </TableRow>
                      );
                    });
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}

const CustomSlices = (props) => {
  const { showTooltipFromEvent, hideTooltip } = useTooltip();

  const [points, setPoints] = useRecoilState(sliceState);

  const handleClick = (slice) => (event) => {
    setPoints(slice.points[0].data);
  };

  return (
    <>
      {props.slices.map((slice, idx) => {
        return (
          <rect
            key={`slice-${idx}`}
            x={slice.x0}
            y={slice.y0}
            width={slice.width}
            height={slice.height}
            stroke="red"
            strokeWidth={0}
            strokeOpacity={0.75}
            fill="red"
            fillOpacity={0}
            onMouseEnter={() => props.setCurrentSlice(slice)}
            onClick={handleClick(slice)}
            onMouseMove={(event) => {
              showTooltipFromEvent(
                React.createElement(props.sliceTooltip, {
                  slice,
                  axis: props.enableSlices
                }),
                event,
                'right'
              );
            }}
            onMouseLeave={() => {
              hideTooltip();
              props.setCurrentSlice(null);
            }}
          />
        );
      })}
    </>
  );
};

const MyResponsiveLine = ({ data }) => {
  const [hiddenIds, setHiddenIds] = useState([]);

  const colors = useOrdinalColorScale({ scheme: 'paired' }, 'id');

  const legendStyle = data.map((item, idx) => {
    const color = colors(item);

    return {
      color: hiddenIds.includes(item.id) ? 'rgba(1, 1, 1, .1)' : color,
      id: item.id,
      label: item.id
    };
  });

  let rows = JSON.parse(JSON.stringify(data));
  rows.filter((item) => {
    item.color = legendStyle.find((t) => t.id === item.id).color;
    if (!hiddenIds.includes(item.id)) {
      return item;
    } else {
      item.data = [];
      return item;
    }
  });

  return (
    <ResponsiveLine
      data={rows}
      margin={{ top: 50, right: 150, bottom: 50, left: 60 }}
      enableSlices={'x'} // 범례기준 x값 보여주기
      xScale={{ type: 'point' }}
      yScale={{
        type: 'linear',
        min: 'auto',
        max: 'auto',
        stacked: false,
        reverse: false
      }}
      // yFormat=" >-.0f"
      yFormat={(value) => `${value} 회`}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        orient: 'bottom',
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: '사용일',
        legendOffset: 36,
        legendPosition: 'middle'
      }}
      axisLeft={{
        orient: 'left',
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: '접속수',
        legendOffset: -40,
        legendPosition: 'middle'
      }}
      colors={{ scheme: 'paired' }}
      pointSize={10}
      pointColor="white"
      pointBorderWidth={2}
      pointBorderColor={{ from: 'serieColor' }}
      enablePointLabel={true}
      pointLabel="y"
      pointLabelYOffset={-12}
      useMesh={true}
      legends={[
        {
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 100,
          translateY: 0,
          itemsSpacing: 0,
          itemDirection: 'left-to-right',
          itemWidth: 80,
          itemHeight: 20,
          symbolSize: 12,
          symbolShape: 'circle',
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1
              }
            }
          ],
          data: legendStyle,
          onClick: (datum) => {
            setHiddenIds((state) => (state.includes(String(datum.id)) ? state.filter((item) => item !== datum.id) : [...state, String(datum.id)]));
          }
        }
      ]}
      layers={['grid', 'markers', 'axes', 'areas', 'crosshair', 'lines', 'points', CustomSlices, 'mesh', 'legends']}
    />
  );
};
